import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { PORTAL_TYPE_TO_ROLE } from "@/lib/rbac";
import { ROLE_LABELS, getRoleDisplayName } from "@/lib/labels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Building2, Users, Wrench, Briefcase, Shield, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

export type AppRole = "super_admin" | "admin" | "employee" | "owner" | "tenant" | "service_provider";

interface RoleConfig {
  label: { ar: string; en: string };
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  color: string;
}

export const roleConfigs: Record<AppRole, RoleConfig> = {
  super_admin: {
    label: ROLE_LABELS.super_admin,
    icon: Shield,
    path: "/super-admin",
    color: "bg-red-500",
  },
  admin: {
    label: ROLE_LABELS.admin,
    icon: Building2,
    path: "/",
    color: "bg-primary",
  },
  employee: {
    label: ROLE_LABELS.employee,
    icon: User,
    path: "/portal/employee",
    color: "bg-green-500",
  },
  owner: {
    label: ROLE_LABELS.owner,
    icon: Building2,
    path: "/portal/owner",
    color: "bg-amber-500",
  },
  tenant: {
    label: ROLE_LABELS.tenant,
    icon: Users,
    path: "/portal/tenant",
    color: "bg-cyan-500",
  },
  service_provider: {
    label: ROLE_LABELS.service_provider,
    icon: Wrench,
    path: "/portal/provider",
    color: "bg-orange-500",
  },
};

interface RoleSwitcherProps {
  currentRole: AppRole;
  onRoleChange: (role: AppRole) => void;
}

export function RoleSwitcher({ currentRole, onRoleChange }: RoleSwitcherProps) {
  const { roles, roleAssignments } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const userRoles = roles.map((r) => r.role);
  const currentConfig = roleConfigs[currentRole];
  const CurrentIcon = currentConfig.icon;

  const handleRoleChange = (role: AppRole) => {
    onRoleChange(role);
    navigate(roleConfigs[role].path);
  };

  // When the user has junction-table assignments, show them instead of bare system roles.
  // Each assignment carries portal_type which determines the portal to navigate to.
  const hasJunctionAssignments = roleAssignments.length > 0;

  const handleAssignmentChange = (assignmentPortalType: string) => {
    const mappedRole = PORTAL_TYPE_TO_ROLE[assignmentPortalType] ?? ("employee" as AppRole);
    onRoleChange(mappedRole);
    navigate(roleConfigs[mappedRole]?.path ?? "/portal/employee");
  };

  // Current assignment (if any) — matched by portal_type → currentRole
  const currentAssignment = roleAssignments.find(
    (a) => PORTAL_TYPE_TO_ROLE[a.role_permissions.portal_type] === currentRole
  );

  // Label for the current display: prefer junction-table role name, fall back to system label
  const currentLabel = currentAssignment
    ? getRoleDisplayName(currentAssignment.role_permissions.role_name, t)
    : t(currentConfig.label.ar, currentConfig.label.en);

  // Single role / single assignment — show static badge
  const totalOptions = hasJunctionAssignments ? roleAssignments.length : userRoles.length;
  if (totalOptions <= 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted">
        <CurrentIcon className="h-4 w-4" />
        <span className="text-sm font-medium">{currentLabel}</span>
      </div>
    );
  }

  // Multiple junction-table assignments — show them in the dropdown
  if (hasJunctionAssignments) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Badge className={`${currentConfig.color} text-white`}>
              <CurrentIcon className="h-3 w-3 me-1" />
              {currentLabel}
            </Badge>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {roleAssignments.map((assignment) => {
            const portalType = assignment.role_permissions.portal_type;
            const mappedRole = PORTAL_TYPE_TO_ROLE[portalType] ?? ("employee" as AppRole);
            const config = roleConfigs[mappedRole] ?? roleConfigs.employee;
            const Icon = config.icon;
            const isActive = assignment.id === currentAssignment?.id;
            return (
              <DropdownMenuItem
                key={assignment.id}
                onClick={() => handleAssignmentChange(portalType)}
                className="gap-2"
              >
                <Badge className={`${config.color} text-white`}>
                  <Icon className="h-3 w-3 me-1" />
                  {getRoleDisplayName(assignment.role_permissions.role_name, t)}
                </Badge>
                {isActive && (
                  <span className="ms-auto text-xs text-muted-foreground">
                    {t("الحالي", "Current")}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Fallback: multiple system roles — original behavior
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Badge className={`${currentConfig.color} text-white`}>
            <CurrentIcon className="h-3 w-3 me-1" />
            {t(currentConfig.label.ar, currentConfig.label.en)}
          </Badge>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {userRoles.map((role) => {
          const config = roleConfigs[role];
          const Icon = config.icon;
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => handleRoleChange(role)}
              className="gap-2"
            >
              <Badge className={`${config.color} text-white`}>
                <Icon className="h-3 w-3 me-1" />
                {t(config.label.ar, config.label.en)}
              </Badge>
              {role === currentRole && (
                <span className="ms-auto text-xs text-muted-foreground">
                  {t("الحالي", "Current")}
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
